import React, { FC, HTMLAttributes, useState } from 'react';
import ArchivedTable from './components/ArchivedTable';
import { MdAdd as AddIcon } from '@react-icons/all-files/md/MdAdd';
import { createUseStyles } from 'src/theming';
import Button from 'src/components/Button';
import TicketTable from './components/TicketTable';
import { Alert, Skeleton } from '@material-ui/lab';
import CreateTicketDialog from '../../components/CreateTicketDialog';
import { CreateTicketOptions, LegacyTicket } from 'src/interfaces/api/Ticket';
import Hidden from 'src/scenes/components/Hidden';
import { Grid } from '@material-ui/core';
import FilterInputs from './components/FilterInputs';

interface Props extends HTMLAttributes<HTMLDivElement> {
  tickets: LegacyTicket[];
  onCreateTicket: (ticketOptions: CreateTicketOptions) => void;
  loading: boolean;
  siteId?: string;
  style?: any;
}

const useStyles = createUseStyles({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 15,
  },
  alert: {
    marginBottom: 15,
  },
  inProgress: {
    borderLeft: '4px solid #f57c73',
    fontSize: 13,
    marginRight: 30,
    paddingLeft: 5,
  },
  toProcess: {
    borderLeft: '4px solid #5b4c91',
    fontSize: 13,
    marginRight: 30,
    paddingLeft: 5,
  },
  table: {
    overflowX: 'auto',
  },
});

const TicketsSection: FC<Props> = ({ style, loading, tickets, hidden, siteId, onCreateTicket }) => {
  const [archivedMode, setArchivedMode] = useState(false);
  const [domainFilter, setDomainFilter] = useState<string>();
  const [showCustomerSatisfaction, setShowCustomerSatisfaction] = useState(false);
  const [isCreateTicketModalOpen, setCreateTicketModalOpen] = useState(false);
  const [startDateFilter, setStartDateFilter] = useState<string>();
  const [endDateFilter, setEndDateFilter] = useState<string>();
  const classes = useStyles();

  const handleDomainFilterChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const newDomain = event.target.value as string;
    setDomainFilter(newDomain === '' ? undefined : newDomain);
  };

  const handleChangeDateFilter = (startDate?: string, endDate?: string) => {
    setStartDateFilter(startDate);
    setEndDateFilter(endDate);
  };

  const handleToggleArchivedMode = () => {
    setArchivedMode(!archivedMode);
  };

  const handleShowCustomerSatisfaction = (event) => {
    setShowCustomerSatisfaction(event.target.checked);
  };

  return (
    <div style={style} hidden={hidden}>
      {!siteId && (
        <Alert severity='warning' className={classes.alert}>
          Vous devez migrer le client pour pouvoir créer de nouveau ticket
        </Alert>
      )}

      {loading && <Skeleton />}

      <Grid container>
        <Grid item xs={12}>
          <Grid container spacing={1} className={classes.header}>
            <Grid item xs={12}>
              <Grid container spacing={1} justifyContent='space-between'>
                <Grid item>
                  <Hidden smDown>
                    <span className={classes.toProcess}>À traiter</span>
                    <span className={classes.inProgress}>En cours de résolution</span>
                  </Hidden>
                </Grid>
                <Grid item>
                  <Button onClick={() => setCreateTicketModalOpen(true)} variant='contained' color='primary'>
                    <AddIcon size={20} />
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              {archivedMode ? (
                <Button onClick={handleToggleArchivedMode} variant='outlined' color='primary'>
                  Sortir des archives
                </Button>
              ) : (
                siteId && (
                  <FilterInputs
                    startDateFilter={startDateFilter}
                    endDateFilter={endDateFilter}
                    domainFilter={domainFilter}
                    showCustomerSatisfaction={showCustomerSatisfaction}
                    onShowCustomerSatisfaction={handleShowCustomerSatisfaction}
                    onDateFilterChange={handleChangeDateFilter}
                    onDomainFilterChange={handleDomainFilterChange}
                  />
                )
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} className={classes.table}>
          <ArchivedTable hidden={!archivedMode} tickets={tickets} loading={loading} />
          <TicketTable
            domainId={domainFilter}
            startDate={startDateFilter}
            showCustomerSatisfaction={showCustomerSatisfaction}
            endDate={endDateFilter}
            siteId={siteId}
            hidden={archivedMode}
          />
        </Grid>

        <Grid item xs={12}>
          <div className={classes.footer}>
            {tickets.length > 0 && (
              <Button onClick={() => handleToggleArchivedMode()} variant='text' color='primary'>
                {archivedMode ? 'Sortir des archives' : 'Allez dans les archives'}
              </Button>
            )}
          </div>
        </Grid>
      </Grid>

      {siteId && (
        <CreateTicketDialog
          siteId={siteId}
          onValidate={onCreateTicket}
          open={isCreateTicketModalOpen}
          onClose={() => setCreateTicketModalOpen(false)}
        />
      )}
    </div>
  );
};

export default TicketsSection;
